<template>
  <div
    class="cart-summary flex flex-col gap-2 lg:gap-4"
    data-testid="cartSummary"
  >
    <CartSection
      :title="$t('Your order summary')"
    >
      <CartItemsList
        v-if="lineItems.length"
        :read-only="isCartFrozen"
        :triggered-in="EVENT_SOURCE.CART_SIDEBAR"
      />
      <div
        v-else
        class="font-normal"
      >
        {{ $t('Your cart is empty') }}
      </div>
    </CartSection>
    <CartSection
      v-if="lineItems.length"
      :title="$t('Delivery')"
    >
      <CartDeliveryInfo
        :approximate-delivery-date="approximateDeliveryDate"
        :shipping-cost-label="shippingCostLabel"
        :is-free="!shippingCost"
      />
    </CartSection>
    <CartSection v-if="lineItems.length">
      <CartDiscountSection :show-coupon-input="!isCartFrozen" />
    </CartSection>
    <SectionTransition
      v-if="lineItems.length"
      :has-content="Boolean(cartRecommendedProducts && cartRecommendedProducts.length)"
      client-only
    >
      <CartSection :title="$t('You may also like')">
        <CartRecommendations
          :recommended-products="cartRecommendedProducts"
          :source-sku="cartRecommendationsSourceProduct?.sku"
        />
      </CartSection>
    </SectionTransition>
  </div>
</template>

<script lang="ts">

import getDeliveryDate from '~~/helpers/getDeliveryDate';
import type { ShippingMethodDeliveryInformation } from '~~/types/checkout/ShippingMethod';
import { EVENT_SOURCE } from '~~/constants/googleTagManager';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { getLastProductWithBreadcrumbs } from '~~/helpers/getLastProductWithBreadcrumbs';
import { SHARE_BASKET_PARAMETER_NAME } from '~~/constants/shareBasket';
import { ROUTES, ROUTES_INFORMATION  } from "~~/constants/routes";
import { isExpondoEmail } from '~~/helpers/shareBasket/paramsParsing';

export default defineComponent({
  name: 'CartSummary',
  emits: ['shareUrlReady'],

  setup(_props, { emit }) {

    const { shippingCostLabel, shippingCost } = useShippingCost();
    const i18n = useI18n();
    const { languageAndCountry } = useLocalization();
    const { selectedShippingMethod } = useShippingMethods();

    const {
      cart,
      isCartFrozen,
    } = useCartExtended();

    const { lineItems, lineItemsWithGuaranteeCombined } = useLineItems();
    const { user } = useUserExtended();

    const deliveryDate = computed(() =>
      getDeliveryDate(languageAndCountry.value, selectedShippingMethod.value as ShippingMethodDeliveryInformation),
    );

    const approximateDeliveryDate = computed(() =>
      i18n.t('approx. Delivery date', { deliveryDate: deliveryDate.value }),
    );

    const { products: cartProducts, loadProducts } = useCheckoutTagManager();
    const {
      recommendedProducts: cartRecommendedProducts,
      search: searchCartRecommendations,
    } = useCartRecommendations();
    const cartRecommendationsSourceProduct = ref<ProductVariantExtended>(null);

    const shareUrl = computed(() =>
    {

      if (!lineItemsWithGuaranteeCombined.value.length) {

        return '';

      }

      const products = lineItemsWithGuaranteeCombined.value.map((lineItem) => {

        const product: { sku: Maybe<string>; quantity: number; extraGuaranteeSku: string | false } = {
          sku: lineItem?.variant?.sku,
          quantity: lineItem.quantity,
          extraGuaranteeSku: false,
        };

        if (lineItem.extraGuarantee?.variant?.sku) {

          product.extraGuaranteeSku = lineItem.extraGuarantee.variant.sku;

        }

        return product;

      });

      const email = user.value?.email;
      const serializedParameters = JSON.stringify(
        email && isExpondoEmail(email)
          ? { products, orderReferrer: email }
          : { products },
      );
      
      const base64Parameters = import.meta.client
        ? btoa(encodeURIComponent(serializedParameters))
        : '';

      const origin = import.meta.client ? window.location.origin : '';
      const slug = ROUTES_INFORMATION[ROUTES.SHARED_BASKET].SLUG;

      return `${origin}${slug}?${SHARE_BASKET_PARAMETER_NAME}=${base64Parameters}`;

    });

    watch(shareUrl, (newShareUrl: string) => {

      emit('shareUrlReady', newShareUrl);

    }, { immediate: true });

    onMounted(async () => {

      if (lineItems.value) {

        await loadProducts();
        cartRecommendationsSourceProduct.value = getLastProductWithBreadcrumbs(cartProducts.value);
        await searchCartRecommendations(cartRecommendationsSourceProduct.value);

      }

    });

    return {
      shippingCost,
      shippingCostLabel,
      approximateDeliveryDate,
      lineItems,
      EVENT_SOURCE,
      cartRecommendedProducts,
      cartRecommendationsSourceProduct,
      cart,
      isCartFrozen,
      shareUrl,
    };

  },

});

</script>
